/* eslint-disable max-len */
import type { ReactNode } from "react"

export interface CryptoProviderChangeModalLinks {
    banxePrivacyPolicy: string
    banxeTerms: string
    paybisPrivacyPolicy: string
    paybisTerms: string
    proWallet: string
}

export const DEFAULT_CRYPTO_PROVIDER_CHANGE_MODAL_LINKS: CryptoProviderChangeModalLinks = {
    banxePrivacyPolicy: "https://banxe.com/privacy-policy/",
    banxeTerms: "https://banxe.com/terms-and-conditions/",
    paybisPrivacyPolicy: "https://paybis.com/policies/privacy/",
    paybisTerms: "https://paybis.com/policies/terms-of-service/",
    proWallet: "https://chatwoot.banxe.com/hc/customer-help-center/articles/1782981769-crypto-services-will-continue-through-pro_wallet",
}

interface CryptoProviderChangeModalContent {
    agreements: {
        banxe: string
        hint: string
        paybis: string
        title: string
    }
    description: {
        intro: string
        proWallet: string
    }
    header: {
        icon: ReactNode
        title: string
    }
    reason: {
        infoIcon: ReactNode
        paybisMica: string
        privacyPolicyDescription: string
        title: string
    }
    submit: {
        error: string
        idle: string
        pending: string
        remindLater: string
    }
}

export const CRYPTO_PROVIDER_CHANGE_MODAL_CONTENT: CryptoProviderChangeModalContent = {
    agreements: {
        banxe: "I agree to the updated <terms>Banxe Terms & Conditions</terms> and <privacyPolicy>Privacy Policy</privacyPolicy>.",
        hint: "Please tick both boxes to continue.",
        paybis: "I agree to the Paybis <terms>Terms and Conditions</terms> and <privacyPolicy>Privacy Policy</privacyPolicy>.",
        title: "Your agreements",
    },
    description: {
        intro: "From 1 July 2026, crypto services on Banxe are provided by Paybis "
            + "(SIA Paybis Europe), authorised under EU MiCA. Your balances and transaction "
            + "history stay the same.",
        proWallet: "Buying and selling continues through your non-custodial ProWallet - only you hold the seed phrase. <proWallet>How ProWallet works</proWallet>.",
    },
    header: {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.5 2C5.98 2 1.5 6.48 1.5 12C1.5 17.52 5.98 22 11.5 22C17.02 22 21.5 17.52 21.5 12C21.5 6.48 17.02 2 11.5 2ZM6.1 11.23C6.27 10.04 6.8 8.97 7.63 8.13C9.63 6.14 12.78 6.03 14.92 7.77V6.82C14.92 6.41 15.26 6.07 15.67 6.07C16.08 6.07 16.42 6.41 16.42 6.82V9.49C16.42 9.9 16.08 10.24 15.67 10.24H13C12.59 10.24 12.25 9.9 12.25 9.49C12.25 9.08 12.59 8.74 13 8.74H13.75C12.2 7.66 10.06 7.81 8.68 9.19C8.08 9.79 7.7 10.57 7.57 11.44C7.52 11.81 7.2 12.08 6.83 12.08C6.79 12.08 6.76 12.08 6.72 12.07C6.33 12.02 6.04 11.64 6.1 11.23ZM15.37 15.87C14.3 16.94 12.9 17.47 11.5 17.47C10.28 17.47 9.07 17.04 8.07 16.23V17.17C8.07 17.58 7.73 17.92 7.32 17.92C6.91 17.92 6.57 17.58 6.57 17.17V14.5C6.57 14.09 6.91 13.75 7.32 13.75H9.99C10.4 13.75 10.74 14.09 10.74 14.5C10.74 14.91 10.4 15.25 9.99 15.25H9.24C10.79 16.33 12.93 16.18 14.31 14.8C14.91 14.2 15.29 13.42 15.42 12.55C15.48 12.14 15.85 11.85 16.27 11.91C16.68 11.97 16.96 12.35 16.91 12.76C16.73 13.97 16.2 15.04 15.37 15.87Z" fill="#7461F0" />
            </svg>
        ),
        title: "Your crypto provider changes on 1 July 2026",
    },
    reason: {
        infoIcon: (
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                    stroke="#9993BD"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                />
                <path d="M12 8H12.01" stroke="#9993BD" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                <path d="M11 12H12V16H13" stroke="#9993BD" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
            </svg>
        ),
        paybisMica: "The current provider's transitional MiCA authorisation expires on 1 July 2026. SIA Paybis Europe is MiCA-authorised and will provide the regulated service from that date.",
        privacyPolicyDescription: "To meet anti-money-laundering and MiCA requirements, your KYC records will be transferred from Neuronext to Paybis. See <banxePrivacyPolicy>Banxe</banxePrivacyPolicy> and <paybisPrivacyPolicy>Paybis</paybisPrivacyPolicy> privacy policies for how your data is handled.",
        title: "Why this change?",
    },
    submit: {
        error: "Unable to accept crypto legal documents.",
        idle: "Agree and continue",
        pending: "Accepting...",
        remindLater: "Remind me later",
    },
}
